import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../../lib/api";

export default function StaffDetail() {
  const { slug } = useParams();
  const [person, setPerson] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api(`/api/staff/${slug}`)
      .then(setPerson)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <main className="min-h-screen bg-slate-50 p-8">Loading staff profile...</main>;

  if (error || !person) {
    return (
      <main className="min-h-screen bg-slate-50 p-8">
        <div className="mx-auto max-w-3xl rounded-3xl bg-white p-8 shadow-sm">
          <p className="font-semibold text-red-700">{error || "Staff profile not found."}</p>
          <Link to="/staff" className="mt-5 inline-block font-bold text-emerald-700">Back to staff directory</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-16">
      <article className="mx-auto max-w-5xl overflow-hidden rounded-[2rem] bg-white shadow-sm ring-1 ring-slate-200">
        <div className="bg-gradient-to-br from-emerald-800 to-slate-950 p-8 text-white md:p-12">
          <Link to="/staff" className="text-sm font-bold text-emerald-100">← Back to Staff Directory</Link>
          <div className="mt-8 flex flex-col gap-6 md:flex-row md:items-end">
            {person.photo_url ? (
              <img src={person.photo_url} alt={person.full_name} className="h-36 w-36 rounded-3xl object-cover ring-4 ring-white/20" />
            ) : (
              <div className="flex h-36 w-36 items-center justify-center rounded-3xl bg-white/10 text-5xl font-black ring-4 ring-white/20">
                {person.full_name?.[0] || "S"}
              </div>
            )}
            <div>
              <p className="text-sm font-black uppercase tracking-[0.25em] text-emerald-200">Staff Profile</p>
              <h1 className="mt-3 text-4xl font-black">{person.title ? `${person.title} ` : ""}{person.full_name}</h1>
              <p className="mt-2 text-lg font-semibold text-emerald-100">{person.position}</p>
              <p className="mt-1 text-white/75">{person.department_name}</p>
            </div>
          </div>
        </div>

        <div className="grid gap-8 p-8 md:grid-cols-[1fr_320px] md:p-12">
          <section>
            <h2 className="text-2xl font-black text-slate-900">Biography</h2>
            <p className="mt-4 whitespace-pre-line leading-8 text-slate-700">{person.biography || "No biography has been added yet."}</p>

            {person.qualifications && (
              <div className="mt-8">
                <h2 className="text-2xl font-black text-slate-900">Qualifications</h2>
                <p className="mt-4 whitespace-pre-line leading-8 text-slate-700">{person.qualifications}</p>
              </div>
            )}

            {person.research_interests && (
              <div className="mt-8">
                <h2 className="text-2xl font-black text-slate-900">Research Interests</h2>
                <p className="mt-4 whitespace-pre-line leading-8 text-slate-700">{person.research_interests}</p>
              </div>
            )}
          </section>

          <aside className="rounded-3xl bg-slate-50 p-6">
            <h2 className="text-lg font-black text-slate-900">Contact Information</h2>
            <dl className="mt-5 space-y-4 text-sm">
              <div><dt className="font-bold text-slate-500">Office</dt><dd className="mt-1 text-slate-900">{person.office || "Not available"}</dd></div>
              <div><dt className="font-bold text-slate-500">Email</dt><dd className="mt-1">{person.email ? <a href={`mailto:${person.email}`} className="font-bold text-emerald-700">{person.email}</a> : "Not available"}</dd></div>
              <div><dt className="font-bold text-slate-500">Phone</dt><dd className="mt-1 text-slate-900">{person.phone || "Not available"}</dd></div>
              <div><dt className="font-bold text-slate-500">Department/Unit</dt><dd className="mt-1 text-slate-900">{person.department_name || "Not available"}</dd></div>
            </dl>
          </aside>
        </div>
      </article>
    </main>
  );
}
